import type { ComputedRef, Ref } from 'vue'
import { computed, unref } from 'vue'

import { keepPreviousData, useMutation, useQuery } from '@tanstack/vue-query'
import { isAxiosError } from 'axios'

import { queryClient } from '@/lib/queryClient'
import { useToast } from '@/store/toast'

import { handleApiErrorToast } from '..'
import { withFailedUsageDailyDaysKept } from './usageDailyCacheUtils'
import { modelsQueryKey } from './useModelQueries'
import { AdminApi } from '../modules/admin'
import { WebsitesApi } from '../modules/websites'

import type { IPaginatedResponse } from '..'
import type {
  IAdminFeedbackListParams,
  IAdminUserListParams,
  IConfigValue,
  TFoundryConfigFlag,
} from '../types/admin'
import type { IAdminUser } from '../types/authUser'
import type { IMessageFeedback } from '../types/chat'
import type { IGpt, IGptAdminSummary } from '../types/gpt'
import type {
  ILLMModelAdmin,
  ILLMModelCircuitState,
  ILLMModelCreateBody,
  ILLMModelPatchBody,
} from '../types/llmModelAdmin'
import type {
  IMcpConnectorAdmin,
  IMcpConnectorAdminList,
  IMcpConnectorCreateBody,
  TMcpConnectorPatchBody,
} from '../types/mcpConnectorAdmin'
import type { IUsageDailyMetricsResponse } from '../types/metrics'
import type { IWebsiteAdminSummary } from '../types/websites'
import type { AxiosError } from 'axios'

export type SystemPromptsValues = {
  systemPrompt: string
  titleSystemPrompt: string
  gptSystemPrompt: string
  personalizedSystemPrompt: string
}

export const adminQueryKeys = {
  all: ['admin'] as const,
  systemPrompts: () => [...adminQueryKeys.all, 'systemPrompts'] as const,
  users: () => [...adminQueryKeys.all, 'users'] as const,
  usersList: (params: IAdminUserListParams) =>
    [...adminQueryKeys.users(), 'list', params] as const,
  usersAll: () => [...adminQueryKeys.users(), 'all'] as const,
  gpts: () => [...adminQueryKeys.all, 'gpts'] as const,
  /**
   * Admin GPT detail — `GET /admin/gpts/{id}`, which always returns the full
   * config. Deliberately **not** `['gpts', 'get', id]`: that key belongs to
   * `useGptQueries.get` (`GET /gpts/{id}`), which strips `config.instructions`
   * and empties `config.files` for a caller without WRITE permission — and an
   * admin gets no bypass there. Sharing the key made the admin edit form render
   * whichever payload landed in the cache first (within `staleTime`), so a GPT
   * merely shared with the admin opened with empty instructions until a reload.
   *
   * Nested under `gpts()` so the existing `invalidateQueries(gpts())` calls keep
   * covering the detail by prefix.
   */
  gpt: (id?: string) =>
    [...adminQueryKeys.gpts(), 'get', id ?? 'none'] as const,
  usageDaily: (start: string, end: string, employeeId?: string | null) =>
    [
      ...adminQueryKeys.all,
      'usageDaily',
      start,
      end,
      employeeId ?? 'all',
    ] as const,
  llmModels: () => [...adminQueryKeys.all, 'llmModels'] as const,
  llmModelCircuitStates: () =>
    [...adminQueryKeys.all, 'llmModelCircuitState'] as const,
  llmModelCircuitState: (id?: string) =>
    [...adminQueryKeys.llmModelCircuitStates(), id ?? 'none'] as const,
  foundryFlag: (key: TFoundryConfigFlag) =>
    [...adminQueryKeys.all, 'foundryFlag', key] as const,
  websites: () => [...adminQueryKeys.all, 'websites'] as const,
  mcpConnectors: () => [...adminQueryKeys.all, 'mcpConnectors'] as const,
  feedback: (params: IAdminFeedbackListParams) =>
    [...adminQueryKeys.all, 'feedback', params] as const,
}

/** Mirrors the API's parse: only these turn a flag off; unset or unknown reads as on. */
const CONFIG_FLAG_OFF_VALUES = ['false', '0', 'no']

export const useAdminQueries = () => {
  // #region System Prompts
  const systemPrompts = () =>
    useQuery<SystemPromptsValues>({
      queryKey: adminQueryKeys.systemPrompts(),
      queryFn: async () => {
        const [system, title, gpt, personalized] = await Promise.all([
          AdminApi.getSystemPrompt(),
          AdminApi.getTitleSystemPrompt(),
          AdminApi.getGPTSystemPrompt(),
          AdminApi.getPersonalizedSystemPrompt(),
        ]).catch(handleApiErrorToast)

        return {
          systemPrompt: system?.value ?? '',
          titleSystemPrompt: title?.value ?? '',
          gptSystemPrompt: gpt?.value ?? '',
          personalizedSystemPrompt: personalized?.value ?? '',
        }
      },
    })

  const updateSystemPrompts = useMutation<void, Error, SystemPromptsValues>({
    mutationFn: async (values) => {
      await Promise.all([
        AdminApi.updateSystemPrompt(values.systemPrompt ?? ''),
        AdminApi.updateTitleSystemPrompt(values.titleSystemPrompt ?? ''),
        AdminApi.updateGptSystemPrompt(values.gptSystemPrompt ?? ''),
        AdminApi.updatePersonalizedSystemPrompt(
          values.personalizedSystemPrompt ?? '',
        ),
      ]).catch(handleApiErrorToast)
    },
    onSuccess: (_, values) => {
      queryClient.setQueryData(adminQueryKeys.systemPrompts(), values)
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.systemPrompts(),
      })
      useToast().onSuccess('System prompts updated successfully')
    },
  })

  // #endregion

  // #region Users
  const users = (params: Ref<IAdminUserListParams>) =>
    useQuery<IPaginatedResponse<IAdminUser>>({
      queryKey: computed(() => adminQueryKeys.usersList(params.value)),
      queryFn: () => AdminApi.getUsers(params.value).catch(handleApiErrorToast),
      // Holds `total` steady while the next page loads.
      placeholderData: keepPreviousData,
    })

  /** Every user, for screens that resolve a `user_id` to a name. */
  const allUsers = () =>
    useQuery<IAdminUser[]>({
      queryKey: adminQueryKeys.usersAll(),
      queryFn: () =>
        AdminApi.getAllUsers()
          .then(({ items }) => items)
          .catch(handleApiErrorToast),
    })

  const updateUserRole = useMutation<
    IAdminUser,
    AxiosError,
    { id: string; role: 'ADMIN' | 'USER' }
  >({
    mutationFn: ({ id, role }) => AdminApi.updateUserRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      useToast().onSuccess('User role updated successfully')
    },
    onError: handleApiErrorToast,
  })

  const deleteUser = useMutation<void, AxiosError, string>({
    mutationFn: (id) => AdminApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      useToast().onSuccess('User deleted successfully')
    },
    onError: handleApiErrorToast,
  })

  const restoreUser = useMutation<void, AxiosError, string>({
    mutationFn: (id) => AdminApi.restoreUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      useToast().onSuccess('User restored successfully')
    },
    onError: handleApiErrorToast,
  })
  // #endregion

  // #region Gpts
  const gpts = (size = 10000) =>
    useQuery<IPaginatedResponse<IGptAdminSummary>>({
      queryKey: adminQueryKeys.gpts(),
      queryFn: () => AdminApi.getGpts({ size }).catch(handleApiErrorToast),
    })
  const getGpt = (id?: ComputedRef<string | undefined>) => {
    return useQuery<IGpt, Error, IGpt>({
      queryKey: computed(() => adminQueryKeys.gpt(unref(id))),
      queryFn: () => {
        const idValue = unref(id)
        if (!idValue) {
          throw new Error('ID is required')
        }
        return AdminApi.getGpt(idValue)
      },
      enabled: computed(() => !!unref(id)),
    })
  }

  const updateGpt = useMutation({
    mutationFn: (data: IGpt | Omit<IGpt, 'config'>) => AdminApi.updateGpt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })

  const removeGpt = useMutation({
    mutationFn: (id: string) => AdminApi.removeGpt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpts'] })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.gpts() })
    },
  })
  // #endregion

  // #region Metrics
  const usageDailyMetrics = (
    activeRange: Ref<{
      start: string
      end: string
      employeeId: string | null
    } | null>,
  ) =>
    useQuery<IUsageDailyMetricsResponse, AxiosError>({
      queryKey: computed(() =>
        activeRange.value
          ? adminQueryKeys.usageDaily(
              activeRange.value.start,
              activeRange.value.end,
              activeRange.value.employeeId,
            )
          : ([...adminQueryKeys.all, 'usageDaily', 'idle'] as const),
      ),
      queryFn: async () => {
        const r = activeRange.value
        if (!r) {
          throw new Error('No date range selected')
        }
        const fresh = await AdminApi.getUsageDailyMetrics({
          start_date: r.start,
          end_date: r.end,
          ...(r.employeeId ? { employee_id: r.employeeId } : {}),
        })
        return withFailedUsageDailyDaysKept(
          fresh,
          queryClient.getQueryData<IUsageDailyMetricsResponse>(
            adminQueryKeys.usageDaily(r.start, r.end, r.employeeId),
          ),
        )
      },
      enabled: computed(() => activeRange.value !== null),
      // The websocket notifies when a day is computed; polling covers a dropped socket.
      refetchInterval: (query) => {
        const computing = query.state.data?.computing_days?.length ?? 0
        return computing > 0 ? 10_000 : false
      },
      retry: (failureCount, err) => {
        const status = err.response?.status
        if (status === 401 || status === 403 || status === 422) {
          return false
        }
        return failureCount < 2
      },
    })
  // #endregion

  // #region Feedback
  const feedback = (params: Ref<IAdminFeedbackListParams>) =>
    useQuery<IPaginatedResponse<IMessageFeedback>>({
      queryKey: computed(() => adminQueryKeys.feedback(params.value)),
      queryFn: () =>
        AdminApi.listFeedback(params.value).catch(handleApiErrorToast),
      // The list is paged server-side: keeping the last page while the next one
      // loads holds `total` steady, so the pagination doesn't collapse mid-fetch.
      placeholderData: keepPreviousData,
      // Feedback keeps arriving from every user while this tab is open. With the
      // app-wide 1-minute staleTime, switching back to a filter already fetched
      // showed its old page — new ratings appeared under "Upvotes" but not "All".
      staleTime: 0,
      refetchOnWindowFocus: true,
    })
  // #endregion

  // #region Published HTML
  const unpublishPublishedHtml = useMutation<void, Error, string>({
    mutationFn: (fileId) => AdminApi.unpublishPublishedHtml(fileId),
    onSuccess: () => {
      useToast().onSuccess('Published site removed successfully')
    },
  })
  // #endregion

  // #region Admin websites
  const adminWebsites = (size = 10000) =>
    useQuery<IPaginatedResponse<IWebsiteAdminSummary>>({
      queryKey: adminQueryKeys.websites(),
      queryFn: () =>
        WebsitesApi.adminList({ size, deleted: 'all' }).catch(
          handleApiErrorToast,
        ),
    })

  const unpublishAdminWebsite = useMutation<void, Error, string>({
    mutationFn: (id) =>
      WebsitesApi.adminUpdate(id, { is_published: false }).then(
        () => undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.websites() })
      useToast().onSuccess('Website unpublished successfully')
    },
  })

  const publishAdminWebsite = useMutation<void, Error, string>({
    mutationFn: (id) =>
      WebsitesApi.adminUpdate(id, { is_published: true }).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.websites() })
      useToast().onSuccess('Website published successfully')
    },
  })

  const softDeleteAdminWebsite = useMutation<void, Error, string>({
    mutationFn: (id) => WebsitesApi.adminRemove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.websites() })
      useToast().onSuccess('Website deleted successfully')
    },
  })

  const restoreAdminWebsite = useMutation<
    void,
    Error,
    { id: string; slug: string }
  >({
    mutationFn: ({ id, slug }) => WebsitesApi.adminRestore(id, { slug }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.websites() })
      useToast().onSuccess('Website restored successfully')
    },
  })

  const hardDeleteAdminWebsite = useMutation<void, Error, string>({
    mutationFn: (id) => WebsitesApi.adminHardRemove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.websites() })
      useToast().onSuccess('Website permanently deleted')
    },
  })
  // #endregion

  // #region LLM models (admin DB rows)
  const invalidateLlmModelRelatedCaches = () => {
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.llmModels() })
    queryClient.invalidateQueries({
      queryKey: adminQueryKeys.llmModelCircuitStates(),
    })
    queryClient.invalidateQueries({ queryKey: modelsQueryKey })
  }

  /**
   * Live breaker state for one model. The row from `llmModels` carries a
   * `circuit_state` snapshot that goes stale while the admin panel is open, so
   * the edit modal reads this instead. Errors stay silent on purpose: the
   * caller falls back to the row snapshot rather than toasting on every open.
   */
  const llmModelCircuitState = (modelId: ComputedRef<string | undefined>) =>
    useQuery<ILLMModelCircuitState, AxiosError>({
      queryKey: computed(() =>
        adminQueryKeys.llmModelCircuitState(unref(modelId)),
      ),
      queryFn: () => {
        const id = unref(modelId)
        if (!id) throw new Error('Model id is required')
        return AdminApi.getLlmModelCircuitState(id)
      },
      enabled: computed(() => !!unref(modelId)),
    })

  const llmModels = (size = 10000) =>
    useQuery<IPaginatedResponse<ILLMModelAdmin>>({
      queryKey: adminQueryKeys.llmModels(),
      queryFn: () =>
        AdminApi.listLlmModels({
          size,
          sort_by: 'position',
          order: 'asc',
        }).catch(handleApiErrorToast),
    })

  const createLlmModel = useMutation<
    ILLMModelAdmin,
    AxiosError,
    ILLMModelCreateBody
  >({
    mutationFn: (body) => AdminApi.createLlmModel(body),
    onSuccess: () => {
      invalidateLlmModelRelatedCaches()
      useToast().onSuccess('LLM model created successfully')
    },
    onError: handleApiErrorToast,
  })

  const patchLlmModel = useMutation<
    ILLMModelAdmin,
    AxiosError,
    { id: string; body: ILLMModelPatchBody }
  >({
    mutationFn: ({ id, body }) => AdminApi.patchLlmModel(id, body),
    onSuccess: () => {
      invalidateLlmModelRelatedCaches()
      useToast().onSuccess('LLM model updated successfully')
    },
    onError: handleApiErrorToast,
  })

  const deleteLlmModel = useMutation<void, AxiosError, string>({
    mutationFn: (id) => AdminApi.deleteLlmModel(id),
    onSuccess: () => {
      invalidateLlmModelRelatedCaches()
      useToast().onSuccess('LLM model deleted successfully')
    },
    onError: handleApiErrorToast,
  })

  const resetLlmModelCircuit = useMutation<
    ILLMModelCircuitState,
    AxiosError,
    string
  >({
    mutationFn: (id) => AdminApi.resetLlmModelCircuitState(id),
    onSuccess: () => {
      invalidateLlmModelRelatedCaches()
      useToast().onSuccess('Circuit breaker reset successfully')
    },
    onError: handleApiErrorToast,
  })
  // #endregion

  // #region LLM Foundry
  const foundryFlag = (key: TFoundryConfigFlag) =>
    useQuery<boolean>({
      queryKey: adminQueryKeys.foundryFlag(key),
      queryFn: async () => {
        try {
          const { value } = await AdminApi.getFoundryFlag(key)
          return !CONFIG_FLAG_OFF_VALUES.includes(value.trim().toLowerCase())
        } catch (error) {
          if (!isAxiosError(error)) throw error
          if (error.response?.status === 404) return true
          return handleApiErrorToast(error)
        }
      },
    })

  const updateFoundryFlag = (
    key: TFoundryConfigFlag,
    successMessage: (value: boolean) => string,
  ) =>
    useMutation<IConfigValue, AxiosError, boolean, { previous?: boolean }>({
      mutationFn: (value) => AdminApi.updateFoundryFlag(key, value),
      // Optimistic so the switch, bound to the cache, flips back on failure.
      onMutate: (value) => {
        const previous = queryClient.getQueryData<boolean>(
          adminQueryKeys.foundryFlag(key),
        )
        queryClient.setQueryData(adminQueryKeys.foundryFlag(key), value)
        return { previous }
      },
      onSuccess: (_, value) => {
        useToast().onSuccess(successMessage(value))
      },
      onError: (error, _, context) => {
        queryClient.setQueryData(
          adminQueryKeys.foundryFlag(key),
          context?.previous,
        )
        return handleApiErrorToast(error)
      },
    })

  const disableFallbacks = () => foundryFlag('disable_fallbacks')
  const updateDisableFallbacks = updateFoundryFlag(
    'disable_fallbacks',
    (isDisabled) =>
      isDisabled
        ? 'LLM Foundry fallbacks disabled'
        : 'LLM Foundry fallbacks enabled',
  )

  const pciMasking = () => foundryFlag('override_pci_masking_enabled')
  const updatePciMasking = updateFoundryFlag(
    'override_pci_masking_enabled',
    (isEnabled) => (isEnabled ? 'PCI masking enabled' : 'PCI masking disabled'),
  )
  // #endregion

  // #region MCP connectors
  const invalidateMcpConnectors = () =>
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.mcpConnectors() })

  const mcpConnectors = () =>
    useQuery<IMcpConnectorAdminList>({
      queryKey: adminQueryKeys.mcpConnectors(),
      queryFn: () => AdminApi.listMcpConnectors().catch(handleApiErrorToast),
    })

  const createMcpConnector = useMutation<
    IMcpConnectorAdmin,
    AxiosError,
    IMcpConnectorCreateBody
  >({
    mutationFn: (body) => AdminApi.createMcpConnector(body),
    onSuccess: (connector) => {
      invalidateMcpConnectors()
      useToast().onSuccess(`${connector.name} connector created`)
    },
    onError: handleApiErrorToast,
  })

  const patchMcpConnector = useMutation<
    IMcpConnectorAdmin,
    AxiosError,
    { id: string; body: TMcpConnectorPatchBody }
  >({
    mutationFn: ({ id, body }) => AdminApi.patchMcpConnector(id, body),
    onSuccess: (connector) => {
      invalidateMcpConnectors()
      useToast().onSuccess(`${connector.name} connector updated`)
    },
    onError: handleApiErrorToast,
  })

  const setMcpConnectorEnabled = useMutation<
    IMcpConnectorAdmin,
    AxiosError,
    { id: string; isEnabled: boolean }
  >({
    mutationFn: ({ id, isEnabled }) =>
      AdminApi.patchMcpConnector(id, { is_enabled: isEnabled }),
    onSuccess: (connector) => {
      invalidateMcpConnectors()
      useToast().onSuccess(
        `${connector.name} connector ${connector.is_enabled ? 'enabled' : 'disabled'}`,
      )
    },
    onError: handleApiErrorToast,
  })
  // #endregion

  return {
    systemPrompts,
    updateSystemPrompts,
    users,
    allUsers,
    updateUserRole,
    deleteUser,
    restoreUser,
    gpts,
    getGpt,
    updateGpt,
    removeGpt,
    usageDailyMetrics,
    feedback,
    unpublishPublishedHtml,
    adminWebsites,
    unpublishAdminWebsite,
    publishAdminWebsite,
    softDeleteAdminWebsite,
    restoreAdminWebsite,
    hardDeleteAdminWebsite,
    llmModels,
    llmModelCircuitState,
    createLlmModel,
    patchLlmModel,
    deleteLlmModel,
    resetLlmModelCircuit,
    disableFallbacks,
    updateDisableFallbacks,
    pciMasking,
    updatePciMasking,
    mcpConnectors,
    createMcpConnector,
    patchMcpConnector,
    setMcpConnectorEnabled,
  }
}
